import * as React from 'react';
import { useDispatch } from 'react-redux';
import * as _ from 'lodash';
import {
  API,
  ConfirmationModal,
  ControlState,
  ControlStyleClass,
  ControlValidation,
  SettingsContextObject,
  Logging,
  Notification,
  NotificationData,
  ResponseError,
  Setting,
  SettingsContext,
  renderCheckboxWithHeading,
  UserInfo,
  PermissionGroup,
  UserContext,
  getEditPermission,
  settingApprovalStartDateOverride,
  toBoolean,
  settingEmailNotificationSettings,
} from '@vcd-ecm-ui/Common';
import Footer from '../../Common/Footer/Footer';
import ApprovalSettingsProps from './ApprovalSettingsProps.interface';
import { updateSettingsObject } from '../../Common/Common';
import '../../Common/common-settings.scss';
import '../certificate-settings.scss';

const controlStateEnabled: ControlState = {
  editable: true,
  disabled: false,
  readOnly: false,
};

const controlStyle: ControlStyleClass = null;

const controlValidation: ControlValidation = {
  requiredFields: null,
  customMessages: null,
};

const ApprovalSettings = ({
  gatewayEndPoint,
  settings,
}: ApprovalSettingsProps): JSX.Element => {
  const [notification, setNotification] = React.useState(null);
  const { settingsContext, setSettingsContext } = React.useContext(SettingsContextObject);
  const [isStartDateOverrideConfirmationOpen, setStartDateOverrideIsConfirmationOpen] = React.useState(false);
  const [isEmailNotificationSettingsConfirmationOpen, setEmailNotificationSettingsIsConfirmationOpen] = React.useState(false);

  // Get the User Context
  const userContext: UserInfo = React.useContext(UserContext);
  const permissionSettingsEdit: boolean = getEditPermission(userContext, PermissionGroup.Settings);

  const dispatch = useDispatch();

  const getStartDateOverride = (): boolean => {
    Logging.Debug('getStartDateOverride');
    const filteredSettings: Setting[] = settings.filter((x: Setting) => x.name === settingApprovalStartDateOverride);

    if (filteredSettings && filteredSettings.length === 1) {
      return filteredSettings[0].value ? toBoolean(filteredSettings[0].value) : false;
    }

    return false;
  };

  const getEmailNotificationSettings = (): boolean => {
    Logging.Debug('getEmailNotificationSettings');
    const filteredSettings: Setting[] = settings.filter((x: Setting) => x.name === settingEmailNotificationSettings);

    if (filteredSettings && filteredSettings.length === 1) {
      return filteredSettings[0].value ? toBoolean(filteredSettings[0].value) : false;
    }

    return false;
  };

  const [startDateOverrideInitial, setStartDateOverrideInitial] = React.useState(getStartDateOverride());
  const [startDateOverride, setStartDateOverride] = React.useState(getStartDateOverride());

  const [emailNotificationSettingsInitial, setEmailNotificationSettingsInitial] = React.useState(getEmailNotificationSettings());
  const [emailNotificationSettings, setEmailNotificationSettings] = React.useState(getEmailNotificationSettings());

  // Get the Updated Settings
  const getUpdatedSettings = (): Setting[] => {
    const updatedSettings: Setting[] = [];

    updatedSettings.push({
      name: settingApprovalStartDateOverride,
      sequenceNumber: 1,
      activeInd: true,
      value: startDateOverride,
      dataType: 'BOOLEAN',
    });

    updatedSettings.push({
      name: settingEmailNotificationSettings,
      sequenceNumber: 1,
      activeInd: true,
      value: emailNotificationSettings,
      dataType: 'BOOLEAN',
    });

    return updatedSettings;
  };

  // Make the API Call to Save the Approval Settings
  const saveApprovalSettings = () => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);
      const updatedSettings: Setting[] = getUpdatedSettings();

      // Save the Settings
      await api.settings.setCertificateSettingsV4(updatedSettings);

      // Update the Settings Object
      const settingsObject: SettingsContext = updateSettingsObject(settingsContext, 'certificates', updatedSettings);

      // Update the Settings Context
      setSettingsContext(settingsObject);

      // Update the Initial Values with the Saved Values
      setStartDateOverrideInitial(startDateOverride);
      setEmailNotificationSettingsInitial(emailNotificationSettings);

      const notificationData: NotificationData = {
        type: 'Information',
        title: 'Approval Settings Saved',
        description: 'The Approval Settings were saved.',
        autoClose: true,
        autoCloseTimeOutMilliseconds: 1000,
        position: 'BottomRight',
      };

      setNotification(notificationData);
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Approval Settings Save Error',
        description: 'Unable to Save Approval Settings.',
        error: error as ResponseError,
      };

      setNotification(notificationData);
    }
  };

  // Save
  const onSave = (): void => {
    // Save
    Logging.Info('Save');

    dispatch(saveApprovalSettings());
  };

  // Cancel
  const onCancel = (): void => {
    Logging.Info('Cancel');

    setStartDateOverride(startDateOverrideInitial);
    setEmailNotificationSettings(emailNotificationSettingsInitial);
  };

  // Close the Notification
  const onNotificationClose = (): void => {
    setNotification(null);
  };

  // Check if the Values has Changed
  const valuesChanged = (): boolean => {
    const equal = (startDateOverride === startDateOverrideInitial) && (emailNotificationSettings === emailNotificationSettingsInitial);

    return !equal;
  };

  // Validate the Values
  const validateValues = (): boolean => true;

  const onStartDateOverrideOKConfirmation = (): void => {
    Logging.Info('OK Confirmation');
    setStartDateOverrideIsConfirmationOpen(false);

    setStartDateOverride(!startDateOverride);
  };

  const onStartDateOverrideCancelConfirmation = (): void => {
    Logging.Info('Cancel Confirmation');
    setStartDateOverrideIsConfirmationOpen(false);
  };

  const onSetStartDateOverride = (value: boolean): void => {
    setStartDateOverrideIsConfirmationOpen(true);
  };

  const onEmailNotificationSettingsOKConfirmation = (): void => {
    Logging.Info('OK Confirmation');
    setEmailNotificationSettingsIsConfirmationOpen(false);

    setEmailNotificationSettings(!emailNotificationSettings);
  };

  const onEmailNotificationSettingsCancelConfirmation = (): void => {
    Logging.Info('Cancel Confirmation');
    setEmailNotificationSettingsIsConfirmationOpen(false);
  };

  const onEmailNotificationSettings = (value: boolean): void => {
    if (!emailNotificationSettings) {
      setEmailNotificationSettings(!emailNotificationSettings);
    } else {
      setEmailNotificationSettingsIsConfirmationOpen(true);
    }
  };

  // eslint-disable-next-line arrow-body-style
  React.useEffect(() => {
    // This Code will run once after the initial Render

    return (): void => {
      // This Code will run once the component unmounts.
      setNotification(null);
      setStartDateOverrideIsConfirmationOpen(false);
    };
  }, []);

  const cancelVisible = true;
  const cancelEnabled = valuesChanged();
  const saveVisible = true;
  const saveEnabled = validateValues() && valuesChanged();

  Logging.Info('Certificate Settings: Approval Settings');
  controlStateEnabled.disabled = !permissionSettingsEdit;

  const confirmationMessageStartDateOverride = startDateOverride
    ? 'This change will override the certificate start date. You will no longer override the initial start date with the date of approval.'
    : 'This change will override the initial certificate start date. Only the approval date will be sent to the calc engine.';

  const confirmationMessageEmailNotificationSettings = emailNotificationSettings
    ? 'Deselecting this will stop email notifications from being sent to customers when certificates are approved.'
    : null;

  return (
    <>
      <div className="approvalSettingsTab">
        {renderCheckboxWithHeading(100, 'startDateOverride', 'Start Date Override', 'Override certificate start date with certificate approval date.',
          startDateOverride, onSetStartDateOverride, controlStateEnabled, controlStyle, controlValidation)}
        {renderCheckboxWithHeading(100, 'emailNotificationSettings', 'Email Notification Settings',
          'Send email notification to customers when certificates are approved.',
          emailNotificationSettings, onEmailNotificationSettings, controlStateEnabled, controlStyle, controlValidation)}
      </div>
      <ConfirmationModal
        isOpen={isStartDateOverrideConfirmationOpen}
        heading="You are making a critical change."
        message={confirmationMessageStartDateOverride}
        okButtonName="Continue"
        onOK={onStartDateOverrideOKConfirmation}
        cancelButtonName="Cancel"
        onCancel={onStartDateOverrideCancelConfirmation}
        confirmationType="Warning"
      />
      <ConfirmationModal
        isOpen={isEmailNotificationSettingsConfirmationOpen}
        heading="Stop sending approval emails?"
        message={confirmationMessageEmailNotificationSettings}
        okButtonName="Continue"
        onOK={onEmailNotificationSettingsOKConfirmation}
        cancelButtonName="Cancel"
        onCancel={onEmailNotificationSettingsCancelConfirmation}
        confirmationType="Information"
      />
      <Footer
        onCancel={onCancel}
        cancelEnabled={cancelEnabled}
        cancelVisible={cancelVisible && permissionSettingsEdit}
        saveVisible={saveVisible && permissionSettingsEdit}
        saveEnabled={saveEnabled}
        onSave={onSave}
      />
      <Notification
        data={notification}
        onNotificationClose={onNotificationClose}
        autoClose
      />
    </>
  );
};

export default ApprovalSettings;
